/**
 * ProjectActivityFeed — surfaces the latest signals for a project.
 *
 * Signal sources (all client-side, no extra API calls):
 *   • Risk assessment (score, freshness, top facet)
 *   • Tickets flagged at-risk / in triage
 *   • Recent ticket activity (source-date in last 7 days)
 *   • Latest inline comment snippets
 *   • Stale tickets (no source update in 5+ days, non-complete)
 */
import { AlertTriangle, MessageCircle, TrendingUp, TrendingDown, Minus, Clock, CheckCircle2 } from 'lucide-react';
import type { Ticket, RiskCache } from '../types';
import { TicketStatusChip } from './StatusChip';
import { Markdown } from './Markdown';
import { CollapsibleText } from './CollapsibleText';
import { normalizeComments } from '../lib/comments';
import { track } from '../lib/telemetry';
import { getRiskBand, getRiskBandDisplay, formatRiskScore, DEFAULT_ALERT_THRESHOLD } from '../lib/riskBand';
import { useUserStore } from '../store/userStore';

interface ProjectActivityFeedProps {
  tickets: Ticket[];
  risk: RiskCache | undefined;
  staleThresholdDays?: number;
}

function relativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  const ms = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(ms / 60_000);
  const h = Math.floor(ms / 3_600_000);
  const d = Math.floor(ms / 86_400_000);
  if (m < 2) return 'just now';
  if (m < 60) return `${m}m ago`;
  if (h < 24) return `${h}h ago`;
  if (d < 30) return `${d}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function sourceDate(t: Ticket): Date {
  return new Date(t.sourceUpdatedAt ?? t.updatedAt);
}

/** Most-recent timestamp from either source or local — used for stale detection. */
function bestUpdatedTs(t: Ticket): number {
  const sourceTs = t.sourceUpdatedAt ? new Date(t.sourceUpdatedAt).getTime() : 0;
  const localTs  = t.updatedAt        ? new Date(t.updatedAt).getTime()        : 0;
  return Math.max(sourceTs, localTs);
}

export function ProjectActivityFeed({
  tickets,
  risk,
  staleThresholdDays = 5,
}: ProjectActivityFeedProps) {
  const alertThreshold = useUserStore((s) => s.sessionConfig?.alertThreshold) ?? DEFAULT_ALERT_THRESHOLD;
  const now = Date.now();
  const staleMs = staleThresholdDays * 86_400_000;
  const recentMs = 7 * 86_400_000;

  // Tickets flagged at-risk or in triage
  const flagged = tickets.filter((t) => t.status === 'at_risk' || t.status === 'triage');

  // Recently moved to complete (last 7 days)
  const recentlyCompleted = tickets.filter(
    (t) => t.status === 'complete' && now - sourceDate(t).getTime() < recentMs,
  );

  // Stale tickets — any non-resolved ticket with no touch in staleThresholdDays
  const DONE = new Set(['complete', 'done', 'closed', 'cancelled', 'wont_fix', 'invalid', 'idea']);
  const staleTickets = tickets.filter((t) => {
    if (DONE.has(t.status)) return false;
    const best = bestUpdatedTs(t);
    return best > 0 && now - best > staleMs;
  });

  // Ticket with the most recent comment. Normalised length, not raw
  // truthiness — an empty comments array must not claim the row.
  const withCommentSorted = tickets
    .filter((t) => normalizeComments(t.recentComments).length > 0)
    .sort((a, b) => sourceDate(b).getTime() - sourceDate(a).getTime());
  const withComment = withCommentSorted.length > 0 ? withCommentSorted[0] : undefined;

  // Nothing to show at all
  if (!risk && flagged.length === 0 && staleTickets.length === 0 && recentlyCompleted.length === 0 && !withComment) {
    return null;
  }

  // ── Generate narrative summary ─────────────────────────────────────────────
  const narrativeParts: string[] = [];

  const activeCount = tickets.filter(
    (t) => !['complete', 'invalid', 'idea'].includes(t.status),
  ).length;

  // Band-first, per the display rule established in v2026.8.4: the sentence
  // states the risk level ONCE, using the same band label the chips use
  // (High/Medium/Low), and carries the raw score as a hover title only —
  // matching the FeedRow badge below. getRiskBand treats a score of 0 (the
  // "unscored" sentinel — see riskBand.ts) the same as null/undefined: no
  // band, so no risk-level claim is made here for either case.
  const overallBand = risk ? getRiskBand(risk.overallScore, alertThreshold) : null;
  let riskSentence: string | null = null;
  let riskScoreTitle: string | undefined;
  if (risk && risk.overallScore != null && overallBand) {
    const bandInfo = getRiskBandDisplay(risk.overallScore, alertThreshold);
    riskSentence = `This project is at ${bandInfo.label.toLowerCase()} risk`;
    const topFacet = [...(risk.facets ?? [])].sort((a, b) => b.score - a.score)[0];
    if (topFacet && getRiskBand(topFacet.score, alertThreshold) !== 'low') {
      riskSentence += ` — the top concern is ${topFacet.name.toLowerCase()}`;
    }
    riskSentence += '.';
    riskScoreTitle = `Score: ${bandInfo.scoreLabel} / 5.0`;
  }

  if (activeCount > 0) {
    const completePct =
      tickets.length > 0
        ? Math.round((tickets.filter((t) => t.status === 'complete').length / tickets.length) * 100)
        : 0;
    narrativeParts.push(
      `There are ${activeCount} active ticket${activeCount !== 1 ? 's' : ''} with ${completePct}% of all work complete.`,
    );
  }

  if (recentlyCompleted.length > 0) {
    narrativeParts.push(
      `${recentlyCompleted.length} ticket${recentlyCompleted.length !== 1 ? 's were' : ' was'} completed this week — good momentum.`,
    );
  }

  if (flagged.length > 0) {
    const atRiskCount = flagged.filter((t) => t.status === 'at_risk').length;
    const triageCount = flagged.filter((t) => t.status === 'triage').length;
    const parts: string[] = [];
    if (atRiskCount) parts.push(`${atRiskCount} at-risk`);
    if (triageCount) parts.push(`${triageCount} in triage`);
    narrativeParts.push(`${parts.join(' and ')} ticket${flagged.length !== 1 ? 's need' : ' needs'} attention.`);
  }

  if (staleTickets.length > 0) {
    narrativeParts.push(
      `${staleTickets.length} ticket${staleTickets.length !== 1 ? 's have' : ' has'} not moved in ${staleThresholdDays}+ days.`,
    );
  }

  const narrative = narrativeParts.join(' ');

  return (
    <div className="glass-card rounded-xl overflow-hidden">
      <div className="px-4 pt-3.5 pb-2.5 border-b border-black/[0.06] dark:border-white/[0.06]">
        <h3 className="text-meta font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          Project Pulse
        </h3>
      </div>

      {/* Narrative paragraph */}
      {(riskSentence || narrative) && (
        <div className="px-4 pt-3 pb-2">
          <p className="text-body text-gray-700 dark:text-gray-300 leading-relaxed">
            {riskSentence && (
              <span title={riskScoreTitle} className="cursor-default">
                {riskSentence}
              </span>
            )}
            {riskSentence && narrative ? ' ' : ''}
            {narrative}
          </p>
        </div>
      )}

      <div className="px-4 py-2 space-y-0 divide-y divide-black/[0.04] dark:divide-white/[0.05]">

        {/* Risk signal */}
        {risk && risk.overallScore != null && (
          <FeedRow
            icon={
              getRiskBand(risk.overallScore, alertThreshold) === 'high' ? (
                <TrendingUp className="h-4 w-4 text-red-400" />
              ) : getRiskBand(risk.overallScore, alertThreshold) === 'low' ? (
                <TrendingDown className="h-4 w-4 text-green-400" />
              ) : (
                <Minus className="h-4 w-4 text-amber-400" />
              )
            }
            time={risk.generatedAt}
          >
            <span className="text-gray-700 dark:text-gray-300">
              Risk score{' '}
              <span
                title={`Score: ${formatRiskScore(risk.overallScore)} / 5.0`}
                className={`font-semibold cursor-default ${getRiskBandDisplay(risk.overallScore, alertThreshold).textClass}`}
              >
                {getRiskBandDisplay(risk.overallScore, alertThreshold).label}
              </span>
              {(() => {
                const sorted = [...(risk.facets ?? [])].sort((a, b) => b.score - a.score);
                const top = sorted.length > 0 ? sorted[0] : undefined;
                if (!top || getRiskBand(top.score, alertThreshold) === 'low') return null;
                return (
                  <span className="text-gray-500 dark:text-gray-500">
                    {' '}— top signal: {top.name.toLowerCase()}
                  </span>
                );
              })()}
              {risk.isStale && (
                <span className="ml-1.5 text-micro text-amber-500 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-1.5 py-0.5 rounded-full border border-amber-200 dark:border-amber-900/30">
                  stale
                </span>
              )}
            </span>
          </FeedRow>
        )}

        {/* At-risk / triage tickets */}
        {flagged.length > 0 && (
          <FeedRow
            icon={<AlertTriangle className="h-4 w-4 text-red-400" />}
            onClick={() => {
              const projectId = flagged[0]?.projectId ?? '';
              const ticketId = flagged.length === 1 ? flagged[0].ticketId : 'multiple';
              track('activity.flagged_ticket_clicked', 'feature_use', { projectId, ticketId });
            }}
          >
            <span className="text-gray-700 dark:text-gray-300">
              {flagged.length === 1 ? (
                <>
                  <span className="font-medium text-gray-900 dark:text-white">{flagged[0].title}</span>
                  {' is '}
                  <TicketStatusChip status={flagged[0].status} />
                </>
              ) : (
                <>
                  <span className="font-medium text-gray-900 dark:text-white">{flagged.length} tickets</span>
                  {' flagged '}
                  {flagged.some((t) => t.status === 'at_risk') && <TicketStatusChip status="at_risk" />}
                  {flagged.some((t) => t.status === 'triage') && (
                    <span className="ml-1"><TicketStatusChip status="triage" /></span>
                  )}
                </>
              )}
            </span>
          </FeedRow>
        )}

        {/* Recently completed */}
        {recentlyCompleted.length > 0 && (
          <FeedRow
            icon={<CheckCircle2 className="h-4 w-4 text-green-400" />}
            time={recentlyCompleted[0] ? (recentlyCompleted[0].sourceUpdatedAt ?? recentlyCompleted[0].updatedAt) : undefined}
          >
            <span className="text-gray-700 dark:text-gray-300">
              <span className="font-medium text-gray-900 dark:text-white">{recentlyCompleted.length}</span>
              {' ticket'}
              {recentlyCompleted.length !== 1 ? 's' : ''} marked{' '}
              <TicketStatusChip status="complete" />
              {' this week'}
            </span>
          </FeedRow>
        )}

        {/* Latest comments — distinct entries (author + body), collapsed to
            an excerpt by default and expandable in place. Keyed by ticketId
            so a refetch that surfaces a different ticket resets expansion. */}
        {withComment && (
          <FeedRow
            icon={<MessageCircle className="h-4 w-4 text-gray-400" />}
            time={withComment.sourceUpdatedAt ?? withComment.updatedAt}
          >
            <div className="min-w-0 text-gray-700 dark:text-gray-300">
              <span className="font-medium text-gray-900 dark:text-white truncate max-w-full inline-block align-bottom">
                {withComment.title}
              </span>
              <CollapsibleText key={withComment.ticketId} collapsedLines={2} className="mt-0.5">
                <div className="space-y-1">
                  {normalizeComments(withComment.recentComments).map((c, i) => (
                    <div key={i} className="text-gray-500 dark:text-gray-400">
                      {c.author && (
                        <span className="font-medium text-gray-600 dark:text-gray-300">{c.author}: </span>
                      )}
                      <Markdown size="xs" className="inline-block align-top">
                        {c.text}
                      </Markdown>
                    </div>
                  ))}
                </div>
              </CollapsibleText>
            </div>
          </FeedRow>
        )}

        {/* Stale tickets */}
        {staleTickets.length > 0 && (
          <FeedRow
            icon={<Clock className="h-4 w-4 text-amber-400" />}
          >
            <span className="text-gray-700 dark:text-gray-300">
              <span className="font-medium text-gray-900 dark:text-white">{staleTickets.length}</span>
              {' ticket'}
              {staleTickets.length !== 1 ? 's' : ''} with no update in{' '}
              <span className="font-medium">{staleThresholdDays}+ days</span>
              {staleTickets.length <= 2 && (
                <span className="text-gray-500 dark:text-gray-500">
                  {': '}
                  {staleTickets.slice(0, 2).map((t, i) => (
                    <span key={t.ticketId}>
                      {i > 0 && ', '}
                      <span className="italic">{t.title}</span>
                    </span>
                  ))}
                </span>
              )}
            </span>
          </FeedRow>
        )}

      </div>
    </div>
  );
}

function FeedRow({
  icon,
  time,
  children,
  onClick,
}: {
  icon?: React.ReactNode;
  time?: string | null;
  children: React.ReactNode;
  onClick?: () => void;
}) {
  return (
    <div
      className={`flex items-start gap-2.5 py-2.5 ${onClick ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-white/[0.03] rounded-lg px-1 -mx-1' : ''}`}
      onClick={onClick}
    >
      {icon && (
        <span className="mt-0.5 flex-shrink-0">{icon}</span>
      )}
      <div className="flex-1 min-w-0 text-meta leading-relaxed">{children}</div>
      {time && (
        <span className="text-meta text-gray-400 dark:text-gray-500 flex-shrink-0 mt-0.5">
          {relativeTime(time)}
        </span>
      )}
    </div>
  );
}
